import React, { useRef, useState, useCallback, useMemo } from 'react';

import { useAnythingTypes } from 'src/queries/anything-types';
import { Input } from 'src/UIComponents';
import styled from 'styled-components';

export const DropdownContainer = styled.div`
  position: relative;
`;

export const SuggestionDropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  min-width: 160px;
  border: 1px ${({ theme }) => theme.colors.N40} solid;
  z-index: 10;
`;

export const StyledSuggestionItem = styled.div`
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  background-color: ${({ theme }) => theme.colors.N0};

  &:hover {
    background-color: ${({ theme }) => theme.colors.N20};
    cursor: pointer;
  }
`;

type SuggestionItemProps = {
  value: string;
  onClick: (type: string) => void;
};

function SuggestionItem({ value, onClick, ...rest }: SuggestionItemProps) {
  const handleOnclick = useCallback(() => {
    onClick(value);
  }, [onClick, value]);

  return (
    <StyledSuggestionItem onClick={handleOnclick} {...rest}>
      {value}
    </StyledSuggestionItem>
  );
}

type NewCustomTypeFieldProps = {
  onSubmit: (newType: string) => void;
};

export function NewCustomTypeField({
  onSubmit,
  ...rest
}: NewCustomTypeFieldProps) {
  const typeEl = useRef(null);
  const [type, setType] = useState('');
  const [typeSuggestionsOpen, setTypeSuggestionOpen] = useState(false);
  const { isLoading, data: anythingTypes = [] } = useAnythingTypes({
    limit: 30,
  });

  const onChangeType = useCallback((event) => {
    setType(event.target.value.toUpperCase());
  }, []);

  const openSuggestions = useCallback(() => {
    setTypeSuggestionOpen(true);
  }, []);
  const handleOnBlur = useCallback(() => {
    onSubmit(type);
    // Hide suggestions
    // Don't really understand this part but had to add a delay, otherwise it will blur and type will not be changed
    setTimeout(() => setTypeSuggestionOpen(false), 100);
  }, [onSubmit, type]);

  // Used when type is clicked (or pressing ENTER)
  const handleTypeChange = useCallback((newType) => {
    setType(newType);
  }, []);

  const allUserTypes = useMemo(() => {
    return anythingTypes.map((anythingType) => anythingType.title);
  }, [anythingTypes]);

  const filteredTypes = useMemo(() => {
    const upperCaseType = type && type.toUpperCase();
    return allUserTypes.filter((anythingType) => {
      const upperCaseTitle = anythingType.toUpperCase();
      return upperCaseTitle.indexOf(upperCaseType) !== -1;
    });
  }, [allUserTypes, type]);

  const handleKeyPress = useCallback(
    (event) => {
      const char = event.which || event.keyCode;
      // Enter || Tab
      if (char === 13 || char === 9) {
        if (filteredTypes.length > 0) {
          const pickedTypename = filteredTypes[0];
          handleTypeChange(pickedTypename.toUpperCase());
        }
      }
      // Escape
      if (char === 27) {
        handleTypeChange('');
      }
    },
    [filteredTypes, handleTypeChange]
  );

  return (
    <div {...rest}>
      <DropdownContainer>
        <Input
          ref={typeEl}
          value={type}
          onChange={onChangeType}
          onFocus={openSuggestions}
          onBlur={handleOnBlur}
          onKeyPress={handleKeyPress}
          placeholder="Type"
          style={{
            flexBasis: '20%',
          }}
        />
        <SuggestionDropdown
          style={{
            display: typeSuggestionsOpen ? 'block' : 'none',
          }}
        >
          {isLoading && <div>Loading...</div>}

          {filteredTypes.map((anythingType) => (
            <SuggestionItem
              key={anythingType}
              onClick={handleTypeChange}
              value={anythingType}
            />
          ))}
        </SuggestionDropdown>
      </DropdownContainer>
    </div>
  );
}
